import { Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import AdminLayout from '../components/admin/AdminLayout.jsx';
import DashboardPage from './admin/DashboardPage.jsx';
import UsersPage from './admin/UsersPage.jsx';
import RolesPage from './admin/RolesPage.jsx';
import LeaveTypesPage from './admin/LeaveTypesPage.jsx';
import HolidaysPage from './admin/HolidaysPage.jsx';
import ChainsPage from './admin/ChainsPage.jsx';
import SettingsPage from './admin/SettingsPage.jsx';
import TemplatesPage from './admin/TemplatesPage.jsx';
import AuditLogPage from './admin/AuditLogPage.jsx';
import ReportsPage from './admin/ReportsPage.jsx';
import RolloverPage from './admin/RolloverPage.jsx';
import ExpenseTypesPage from './admin/ExpenseTypesPage.jsx';
import PositionsPage from './admin/PositionsPage.jsx';
import AdminExpenseReportsPage from './admin/AdminExpenseReportsPage.jsx';
import AdminExpenseRequestsPage from './admin/AdminExpenseRequestsPage.jsx';
import MonthlyExpenseReportPage from './admin/MonthlyExpenseReportPage.jsx';
import ReceiptRecoveryPage from './admin/ReceiptRecoveryPage.jsx';
import ContractsPage from './admin/ContractsPage.jsx';
import DocumentTypesPage from './admin/DocumentTypesPage.jsx';
import DocumentsPage from './admin/DocumentsPage.jsx';
import AdminVisaRequestsPage from './admin/AdminVisaRequestsPage.jsx';
import TravelRequestsPage from './admin/TravelRequestsPage.jsx';
import LeaveRequestsPage from './admin/LeaveRequestsPage.jsx';
import BalanceAdjustmentsPage from './admin/BalanceAdjustmentsPage.jsx';
import ExpenseTemplatePage from './admin/ExpenseTemplatePage.jsx';
import HelpPage from './employee/HelpPage.jsx';
import SetupWizard from '../components/onboarding/SetupWizard.jsx';
import { adminApi } from '../lib/api.js';
import { useCurrentUser } from '../hooks/useCurrentUser.jsx';

// Admin routes in nav order, each with the section that grants it. Drives
// both the per-route access guard and the "where does a non-admin land"
// redirect. Keep in sync with NAV_SECTIONS in AdminLayout.
const ROUTE_SECTIONS = [
  ['users', 'people'],
  ['roles', 'roles'],
  ['contracts', 'contracts'],
  ['leave-types', 'leaveConfig'],
  ['holidays', 'leaveConfig'],
  ['leave-requests', 'leaveRequests'],
  ['balance-adjustments', 'balances'],
  ['rollover', 'balances'],
  ['expense-requests', 'expenses'],
  ['expense-reports', 'expenses'],
  ['expense-monthly-report', 'expenses'],
  ['expense-recovery', 'expenses'],
  ['expense-types', 'expenseConfig'],
  ['positions', 'people'],
  ['expense-template', 'expenseConfig'],
  ['documents', 'documents'],
  ['document-types', 'documents'],
  ['visas', 'visas'],
  ['travel', 'travel'],
  ['chains', 'workflow'],
  ['settings', 'settings'],
  ['templates', 'settings'],
  ['reports', 'reports'],
  ['audit', 'reports'],
];

// First admin route the user can reach, in nav order. Falls back to the
// employee view when they have no admin sections at all.
function firstAccessiblePath(can) {
  const hit = ROUTE_SECTIONS.find(([, section]) => can(section));
  return hit ? `/admin/${hit[0]}` : '/';
}

// Per-route access guard. Admins pass everything. Others need the route's
// section (adminOnly routes — the Dashboard — are admins only). We wait for
// the profile to load so a protected page never briefly renders (and fires
// its API calls) before the redirect kicks in.
function SectionRouteGuard({ section, adminOnly, children }) {
  const { isAdmin, can, loading } = useCurrentUser();
  if (loading) return null;
  if (isAdmin) return children;
  if (adminOnly || (section && !can(section))) {
    return <Navigate to={firstAccessiblePath(can)} replace />;
  }
  return children;
}

export default function AdminPage() {
  const [showWizard, setShowWizard] = useState(false);
  const { isAdmin } = useCurrentUser();

  // First-time setup wizard is for full admins only.
  useEffect(() => {
    if (!isAdmin) return;
    try {
      if (localStorage.getItem('prohr_setup_done') === 'true') return;
    } catch {}
    adminApi.listLeaveTypes().then((res) => {
      const types = res.leaveTypes || [];
      if (types.length === 0) setShowWizard(true);
    }).catch(() => {});
  }, [isAdmin]);

  const g = (section, el) => <SectionRouteGuard section={section}>{el}</SectionRouteGuard>;

  return (
    <>
      {showWizard && <SetupWizard onComplete={() => setShowWizard(false)} />}
      <Routes>
        <Route element={<AdminLayout />}>
          <Route index element={<SectionRouteGuard adminOnly><DashboardPage /></SectionRouteGuard>} />
          <Route path="users" element={g('people', <UsersPage />)} />
          <Route path="roles" element={g('roles', <RolesPage />)} />
          <Route path="contracts" element={g('contracts', <ContractsPage />)} />
          <Route path="leave-types" element={g('leaveConfig', <LeaveTypesPage />)} />
          <Route path="holidays" element={g('leaveConfig', <HolidaysPage />)} />
          <Route path="leave-requests" element={g('leaveRequests', <LeaveRequestsPage />)} />
          <Route path="balance-adjustments" element={g('balances', <BalanceAdjustmentsPage />)} />
          <Route path="rollover" element={g('balances', <RolloverPage />)} />
          <Route path="expense-requests" element={g('expenses', <AdminExpenseRequestsPage />)} />
          <Route path="expense-reports" element={g('expenses', <AdminExpenseReportsPage />)} />
          <Route path="expense-monthly-report" element={g('expenses', <MonthlyExpenseReportPage />)} />
          <Route path="expense-recovery" element={g('expenses', <ReceiptRecoveryPage />)} />
          <Route path="expense-types" element={g('expenseConfig', <ExpenseTypesPage />)} />
          <Route path="positions" element={g('people', <PositionsPage />)} />
          <Route path="expense-template" element={g('expenseConfig', <ExpenseTemplatePage />)} />
          <Route path="documents" element={g('documents', <DocumentsPage />)} />
          <Route path="document-types" element={g('documents', <DocumentTypesPage />)} />
          <Route path="visas" element={g('visas', <AdminVisaRequestsPage />)} />
          <Route path="travel" element={g('travel', <TravelRequestsPage />)} />
          <Route path="chains" element={g('workflow', <ChainsPage />)} />
          <Route path="settings" element={g('settings', <SettingsPage />)} />
          <Route path="templates" element={g('settings', <TemplatesPage />)} />
          <Route path="reports" element={g('reports', <ReportsPage />)} />
          <Route path="audit" element={g('reports', <AuditLogPage />)} />
          <Route path="help" element={<HelpPage />} />
          <Route path="*" element={<AdminFallbackRedirect />} />
        </Route>
      </Routes>
    </>
  );
}

// Unknown /admin/* path → send admins to the dashboard, others to their
// first accessible page (or the employee view).
function AdminFallbackRedirect() {
  const { isAdmin, can, loading } = useCurrentUser();
  if (loading) return null;
  return <Navigate to={isAdmin ? '/admin' : firstAccessiblePath(can)} replace />;
}
